'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Assignment,
  AssignmentTurnedIn,
  NotificationsActive,
  BarChart,
  TrendingUp,
  PhoneAndroid,
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  Link as LinkIcon,
  People,
  FormatListBulleted,
  FolderOpen,
  Edit,
  Groups,
  VerifiedUser,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import BrandName from '../../components/BrandName';
import WorkflowStepper from '../../components/WorkflowStepper';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  { title: 'Create', description: 'Spawn an action plan from a finding or create standalone' },
  { title: 'Assign', description: 'Route to a user, role, or committee with a due date' },
  { title: 'Surface', description: "Appears on assignees' due-items lists based on urgency" },
  { title: 'Work & Document', description: 'Record actions taken and status comments' },
  { title: 'Complete & Review', description: 'Close and review follow-through on the dashboard' }
];

const painPoints = [
  'Corrective actions recorded in minutes and emails with no owner or due date',
  'No connection between the action and the grievance, incident, or audit that triggered it',
  'Follow-through dependent on whoever remembers to ask at the next meeting',
  'No visibility into what\'s overdue—until a surveyor finds the unresolved finding',
  'Repeat findings because the fix was discussed but never completed',
  'Leadership unable to answer a simple question: how many open actions do we have?',
];

const capabilities = [
  {
    icon: LinkIcon,
    title: 'Context-Linked Corrective Actions',
    description: 'Every action plan links to the record that triggered it—a grievance, an incident, an audit, or a committee meeting decision—so the corrective action and its source stay connected for review and survey.',
  },
  {
    icon: AssignmentTurnedIn,
    title: 'Clear Objective and Task',
    description: 'Each action plan states its objective and the task to perform—no vague "follow up" entries. What needs to happen, and why, is explicit on every record.',
  },
  {
    icon: People,
    title: 'Assignment to User, Role, or Committee',
    description: 'Assign actions to one or more specific users, an entire role, or a committee. Work routes to the right people even when assignments span departments and shifts.',
  },
  {
    icon: WarningAmber,
    title: 'Due Dates with Urgency Buckets',
    description: 'Every action carries a due date and automatically sorts into overdue, due soon, or upcoming on assignees\' task lists—so priorities are obvious without anyone compiling a list.',
  },
  {
    icon: FormatListBulleted,
    title: 'Daily Task List Integration',
    description: 'Action plans surface on the Due Items dashboard alongside staff members\' other assigned work. Assignees open, update, and complete actions right from their daily list.',
  },
  {
    icon: FolderOpen,
    title: 'My Action Plans Workspace',
    description: 'A dedicated workspace where staff see their actions organized by status—new, in progress, completed—with search and filtering, plus the ability to create new actions with the right context attached.',
  },
  {
    icon: Edit,
    title: 'Progress Documentation',
    description: 'Assignees record actions taken, status comments, and completion dates as work happens—building a follow-through record, not just a checkbox.',
  },
  {
    icon: BarChart,
    title: 'Leadership Dashboard',
    description: 'The Action Plans Dashboard tracks total, open, and overdue actions plus on-time completion percentage—with breakdowns by status, context, assignee, and priority—so leadership sees follow-through across the facility at a glance.',
  },
  {
    icon: Groups,
    title: 'Committee Meeting Follow-Up',
    description: 'Actions created in committee meetings appear in the meeting record and flow into meeting minutes and PDFs—then come back for review at the next meeting with current status.',
  },
  {
    icon: TrendingUp,
    title: 'KPI-Triggered Actions',
    description: 'Configure audit and pathway KPI thresholds to generate action plans—with a defined name, due window, and assignee—when performance misses target.',
  },
  {
    icon: NotificationsActive,
    title: 'Nudges & Reminders',
    description: 'Send targeted nudges to assignees on outstanding actions—a documented prompt instead of a hallway reminder.',
  },
  {
    icon: PhoneAndroid,
    title: 'Mobile Access',
    description: 'View, create, update, and complete action plans from the CareAscendium mobile app—including from incident, grievance, and meeting contexts—so follow-through doesn\'t wait for a desk.',
  },
];

const roles = [
  {
    title: 'Administrators & DONs',
    benefit: 'Live dashboard of open, overdue, and completed actions across the facility',
  },
  {
    title: 'Department heads',
    benefit: 'Actions assigned to their roles surface automatically on their teams\' task lists',
  },
  {
    title: 'QAPI & quality leaders',
    benefit: 'Corrective actions linked to source findings, with on-time completion metrics',
  },
  {
    title: 'Frontline assignees',
    benefit: 'One daily list with clear objectives, due dates, and simple status updates',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'Consistent corrective-action discipline and visibility across every facility',
  },
];

const outcomes = [
  { value: 'Tracked', label: 'On-time completion', detail: 'On-time completion percentage on the leadership dashboard' },
  { value: 'Alerted', label: 'Overdue visibility', detail: 'Overdue actions flagged automatically on task lists and dashboards' },
  { value: 'Assigned', label: 'Clear ownership', detail: 'Every action assigned to a user, role, or committee with a due date' },
  { value: 'Linked', label: 'Source traceability', detail: 'Each action linked to the grievance, incident, audit, or meeting behind it' },
  { value: 'Logged', label: 'Follow-through evidence', detail: 'Actions taken, status comments, and completion dates on every record' },
  { value: 'Resolved', label: 'Repeat-finding control', detail: 'Closed-loop corrective action instead of rediscovered problems' },
];

const integrations = [
  'Grievance Management',
  'Incident Investigation',
  'Committee Meetings',
  'Audits',
  'Due Items',
  'Dashboards',
];

const faqs = [
  {
    q: 'Where can action plans be created from?',
    a: 'From grievances, incident investigations, committee meetings, and audits—or standalone from the My Action Plans workspace and the dashboard. Each action keeps a link to its source record.',
  },
  {
    q: 'Who can an action be assigned to?',
    a: 'A specific user (or several), an entire role, or a committee. Assignees see the action on their daily due-items list automatically.',
  },
  {
    q: 'How do assignees know what\'s most urgent?',
    a: 'Actions sort into overdue, due soon, and upcoming buckets on the Due Items list, and can be filtered by status and priority in My Action Plans.',
  },
  {
    q: 'How does leadership track follow-through?',
    a: 'The Action Plans Dashboard shows total, open, and overdue actions and on-time completion percentage, with breakdowns by status, context, assignee, and priority.',
  },
  {
    q: 'Can actions be generated automatically?',
    a: 'Yes. Audit and pathway KPI thresholds can be configured to generate an action plan—with a name, due window, and assignee—when results miss target.',
  },
  {
    q: 'How do committee action items stay alive between meetings?',
    a: 'Actions created in a meeting appear in the meeting record and minutes, stay on assignees\' task lists, and come back for status review at the next meeting. Nudges can prompt assignees in between.',
  },
  {
    q: 'Does it work on mobile?',
    a: 'Yes. Staff can view, create, update, and complete action plans from the CareAscendium mobile app on iOS and Android.',
  },
];

export default function ActionPlansClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Action Plan & Corrective Action Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Action Plans"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            Every Finding Becomes an Assigned, Due-Dated, Tracked Action—Until It's Done
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Action Plans turn grievance findings, incident conclusions, audit results, and committee decisions into accountable corrective actions—assigned to a person, role, or committee, surfaced on daily task lists, and tracked to completion on web and mobile.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with "We'll Take Care of It"
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              Every investigation, audit, and committee meeting produces things that must get done. In most SNFs, those actions live in meeting minutes, sticky notes, and good intentions. Nobody owns them, nothing tracks them, and three months later the same finding shows up again—except now there's a paper trail showing the facility knew and didn't follow through.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;A documented finding with no completed corrective action isn't just an open task—it's evidence the facility knew and didn't act.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Workflow. From Finding to Finished.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> Action Plans give every corrective action a structure: an objective, a task, an owner, and a due date—linked to the record that triggered it. Actions surface automatically on assignees' daily task lists, progress is documented as work happens, and leadership watches completion rates and overdue items on a live dashboard.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Platform
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Action Plans are the follow-through layer for the entire platform:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Findings that actually get fixed.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Documentation You Can Stand Behind
          </Typography>
          <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
            <BrandName variant="light" style={{ fontSize: 'inherit' }} /> supports the closed-loop corrective action discipline surveyors look for:
          </Typography>
          <Box sx={{ textAlign: 'left', width: 'fit-content', mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Every corrective action traceable to its source finding or decision</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Named accountability—user, role, or committee—with due dates on every action</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Documented actions taken and completion dates, not just status changes</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Overdue actions visible to leadership before they become survey findings</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Committee action items recorded in meeting minutes and reviewed on cadence</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>A complete audit trail of who changed what, and when</Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Close the Loop on Every Finding?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> turns investigations, audits, and meeting decisions into assigned, due-dated, completed corrective actions—with the dashboard to prove it.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
